// @ts-nocheck
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { SensorData, SensorReading, fakeSensors } from '../../types/SensorData';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Sidebar } from 'primereact/sidebar';
import { _fetchTagList, _fetchEnginList, _getTagHistories } from '../../api/api';
import {OlangItem} from '../../../shared/Olang/user-interface/OlangItem/OlangItem';
import HistoriqueColumnSelector from './HistoriqueColumnSelector.jsx';
import { ALL_COLUMNS, HISTORIQUE_COLUMNS_STORAGE_KEY } from './HistoriqueColumnConfig.js';
// @ts-ignore - PageHeader is a JSX component
import { PageHeader } from '../../../shared/PageHeader/PageHeader';
import { DataTableComponent } from '@omniyat/reactcomponents';

interface HistoriqueEntry {
  timestamp: string;
  temperature: number;
  humidity: number;
  tempMinExceeded: boolean;
  tempMaxExceeded: boolean;
  humMinExceeded: boolean;
  humMaxExceeded: boolean;
  // Données des capteurs de porte
  porte1Ouvert?: boolean;
  porte2Ouvert?: boolean;
}

const Historique: React.FC = () => {
  // Références pour les exports
  const dt = useRef<any>(null);
  const toast = useRef<Toast>(null);
  
  // État pour les capteurs chargés depuis l'API
  const [capteurs, setCapteurs] = useState<SensorData[]>([]);
  const [loadingCapteurs, setLoadingCapteurs] = useState(true);
  const [selectedSensor, setSelectedSensor] = useState<SensorData | null>(null);
  
  // État pour les historiques du capteur sélectionné
  const [sensorHistoriques, setSensorHistoriques] = useState<SensorReading[]>([]);
  const [loadingHistoriques, setLoadingHistoriques] = useState(false);
  
  // États pour le filtrage global
  const [globalFilter, setGlobalFilter] = useState<string>('');
  
  // États pour la gestion des colonnes
  const [visibleColumns, setVisibleColumns] = useState<any[]>([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Fonctions pour la gestion du localStorage
  const loadColumnsFromStorage = () => {
    try {
      const saved = localStorage.getItem(HISTORIQUE_COLUMNS_STORAGE_KEY);
      if (saved) {
        const savedColumns = JSON.parse(saved);
        // Vérifier que les colonnes sauvegardées existent toujours
        const validColumns = savedColumns.filter((savedCol: any) => 
          ALL_COLUMNS.some(col => col.field === savedCol.field)
        );
        return validColumns.length > 0 ? validColumns : ALL_COLUMNS.filter(col => col.defaultVisible);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des colonnes:', error);
    }
    return ALL_COLUMNS.filter(col => col.defaultVisible);
  };

  const saveColumnsToStorage = (columns: any[]) => {
    try {
      localStorage.setItem(HISTORIQUE_COLUMNS_STORAGE_KEY, JSON.stringify(columns));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des colonnes:', error);
    }
  };

  const handleColumnChange = (newColumns: any[]) => {
    // S'assurer que l'horodatage est toujours visible
    const timestampColumn = ALL_COLUMNS.find(col => col.field === 'timestamp');
    if (timestampColumn && !newColumns.some(col => col.field === 'timestamp')) {
      newColumns = [timestampColumn, ...newColumns];
    }
    
    setVisibleColumns(newColumns);
    saveColumnsToStorage(newColumns);
  };
  
  // Initialiser avec les dernières 24h par défaut
  const getDefault24HourRange = () => {
    const now = new Date()
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    return { start: yesterday, end: now }
  }
  
  const [startDate, setStartDate] = useState<Date | null>(() => getDefault24HourRange().start);
  const [endDate, setEndDate] = useState<Date | null>(() => getDefault24HourRange().end);

  // State pour savoir si l'utilisateur a sélectionné une période personnalisée
  const [isCustomPeriod, setIsCustomPeriod] = useState<boolean>(false);
  
  // State pour la navigation par jour (utilisé quand pas de période personnalisée)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Fonctions d'export
  const exportCSV = () => {
    if (dt.current) {
      dt.current.exportCSV();
      toast.current?.show({
        severity: 'success',
        summary: 'Export réussi',
        detail: 'Données exportées en CSV',
        life: 3000
      });
    }
  };

  const exportPdf = () => {
    import('jspdf').then((jsPDF) => {
      import('jspdf-autotable').then(() => {
        const doc = new jsPDF.default();
        
        // Titre du document
        doc.setFontSize(18);
        doc.text('Historique des Capteurs', 14, 22);
        
        // Informations sur le capteur et la période
        doc.setFontSize(12);
        doc.text(`Capteur: ${selectedSensor?.label || 'N/A'}`, 14, 35);
        doc.text(`Période: ${startDate?.toLocaleDateString('fr-FR')} - ${endDate?.toLocaleDateString('fr-FR')}`, 14, 45);
        doc.text(`Nombre d'enregistrements: ${historique.length}`, 14, 55);
        
        // Préparer les données pour le tableau
        const exportColumns = [
          { title: 'Horodatage', dataKey: 'timestamp' },
          { title: 'Température (°C)', dataKey: 'temperature' },
          { title: 'Humidité (%)', dataKey: 'humidity' },
          { title: 'Dépassements', dataKey: 'depassements' }
        ];
        
        const exportData = historique.map(entry => ({
          timestamp: new Date(entry.timestamp).toLocaleString('fr-FR'),
          temperature: entry.temperature.toFixed(2),
          humidity: entry.humidity.toFixed(2),
          depassements: [
            entry.tempMinExceeded ? `Temp < ${selectedSensor?.temperatureMin}°C` : '',
            entry.tempMaxExceeded ? `Temp > ${selectedSensor?.temperatureMax}°C` : '',
            entry.humMinExceeded ? `Hum < ${selectedSensor?.humidityMin}%` : '',
            entry.humMaxExceeded ? `Hum > ${selectedSensor?.humidityMax}%` : ''
          ].filter(Boolean).join(', ') || 'Aucun'
        }));
        
        (doc as any).autoTable({
          columns: exportColumns,
          body: exportData,
          startY: 65,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [41, 128, 185] },
          alternateRowStyles: { fillColor: [245, 245, 245] }
        });
        
        doc.save(`historique_${selectedSensor?.label || 'capteur'}_${new Date().toISOString().split('T')[0]}.pdf`);
        
        toast.current?.show({
          severity: 'success',
          summary: 'Export réussi',
          detail: 'Données exportées en PDF',
          life: 3000
        });
      });
    });
  };

  const exportExcel = () => {
    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(
        historique.map(entry => ({
          'Horodatage': new Date(entry.timestamp).toLocaleString('fr-FR'),
          'Température (°C)': entry.temperature.toFixed(2),
          'Humidité (%)': entry.humidity.toFixed(2),
          'Temp Min Dépassée': entry.tempMinExceeded ? 'Oui' : 'Non',
          'Temp Max Dépassée': entry.tempMaxExceeded ? 'Oui' : 'Non',
          'Hum Min Dépassée': entry.humMinExceeded ? 'Oui' : 'Non',
          'Hum Max Dépassée': entry.humMaxExceeded ? 'Oui' : 'Non',
          'Capteur': selectedSensor?.label || 'N/A',
          'Période Début': startDate?.toLocaleString('fr-FR') || 'N/A',
          'Période Fin': endDate?.toLocaleString('fr-FR') || 'N/A'
        }))
      );
      
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, 'Historique');
      xlsx.utils.sheet_add_aoa(worksheet, [
        ['Historique des Capteurs'],
        [`Capteur: ${selectedSensor?.label || 'N/A'}`],
        [`Période: ${startDate?.toLocaleDateString('fr-FR')} - ${endDate?.toLocaleDateString('fr-FR')}`],
        [`Nombre d'enregistrements: ${historique.length}`],
        []
      ], { origin: 'A1' });
      
      xlsx.writeFile(workbook, `historique_${selectedSensor?.label || 'capteur'}_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Export réussi',
        detail: 'Données exportées en Excel',
        life: 3000
      });
    });
  };
  const goToPreviousDay = () => {
    if (!isCustomPeriod) {
      const prevDay = new Date(selectedDate);
      prevDay.setDate(prevDay.getDate() - 1);
      setSelectedDate(prevDay);
      
      // Mettre à jour les dates pour les dernières 24h du jour précédent
      const endOfDay = new Date(prevDay);
      endOfDay.setHours(23, 59, 59, 999);
      const startOfDay = new Date(prevDay);
      startOfDay.setHours(0, 0, 0, 0);
      
      setStartDate(startOfDay);
      setEndDate(endOfDay);
    }
  };

  const goToNextDay = () => {
    if (!isCustomPeriod) {
      const nextDay = new Date(selectedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setSelectedDate(nextDay);
      
      // Mettre à jour les dates pour les dernières 24h du jour suivant
      const endOfDay = new Date(nextDay);
      endOfDay.setHours(23, 59, 59, 999);
      const startOfDay = new Date(nextDay);
      startOfDay.setHours(0, 0, 0, 0);
      
      setStartDate(startOfDay);
      setEndDate(endOfDay);
    }
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isFutureDate = (date: Date) => {
    const today = new Date();
    return date > today;
  };

  // Gestionnaires pour les sélecteurs de dates personnalisées
  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    setIsCustomPeriod(true);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
    setIsCustomPeriod(true);
  };

  // Fonction pour revenir au mode 24h par défaut
  const resetTo24HourMode = () => {
    const range = getDefault24HourRange();
    setStartDate(range.start);
    setEndDate(range.end);
    setSelectedDate(new Date());
    setIsCustomPeriod(false);
  };

  // Charger les capteurs depuis l'API
  const loadCapteurs = async () => {
    try {
      setLoadingCapteurs(true);
      console.log('📊 Chargement des capteurs pour Historique...');
      
      const response = await _fetchEnginList();
      const result = response.data;
      console.log('📊 Résultat _fetchEnginList:', result);
      
      if (result?.success && Array.isArray(result.result)) {
        const capteursList = result.result.map((engin: any): SensorData => ({
          // Required SensorData fields
          macAddr: engin.tagname || engin.macAddr || engin.uid || 'N/A',
          label: engin.title || engin.label || engin.reference || `Engin-${engin.uid}`,
          rssi: engin.rssi || 0,
          temperature: engin.temperature || 0,
          humidity: engin.humidity || 0,
          battery: parseInt(engin.batteries) || 0,
          temperatureMin: engin.temperatureThresholdMin || engin.temperatureMin || -5,
          temperatureMax: engin.temperatureThresholdMax || engin.temperatureMax || 25,
          humidityMin: engin.humidityThresholdMin || engin.humidityMin || 30,
          humidityMax: engin.humidityThresholdMax || engin.humidityMax || 70,
          alertDelay: 30,
          lastSeen: engin.lastSeenAt || engin.tagDate || new Date().toISOString(),
          alertes: false,
          rapports: true,
          actif: engin.sysActive === 1,
          historique: [],
          alertesList: [],
          // New engin fields
          tagname: engin.tagname,
          uid: engin.uid,
          id: engin.id,
          title: engin.title,
          reference: engin.reference,
          name: engin.title || engin.label
        }));
        
        console.log('📊 Capteurs transformés:', capteursList);
        setCapteurs(capteursList);
      } else {
        console.warn('📊 Format de réponse inattendu, utilisation des données de test');
        setCapteurs(fakeSensors);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des capteurs:', error);
      setCapteurs(fakeSensors);
    } finally {
      setLoadingCapteurs(false);
    }
  };

  // Charger l'historique d'un capteur spécifique
  const loadSensorHistorique = async (sensor: SensorData) => {
    if (!sensor || !startDate || !endDate) return;
    
    try {
      setLoadingHistoriques(true);
      console.log('📈 Chargement historique pour:', sensor.label);
      
      // Fonction pour formater la date en heure locale (pas UTC)
      const formatLocalDateTime = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };
      
      // Format dates for API (YYYY-MM-DD HH:mm:ss) en heure locale
      const fromDate = formatLocalDateTime(startDate);
      const toDate = formatLocalDateTime(endDate);
      
      console.log('📈 Dates formatées (heure locale):', { fromDate, toDate });
      console.log('📈 Dates originales:', { 
        startDate: startDate.toString(), 
        endDate: endDate.toString() 
      });
      
      const requestData = {
        tags: [sensor.tagname || sensor.label], // tagname pour tags
        from: fromDate,
        to: toDate,
        srcObject: 'engin',
        srcIds: [sensor.uid || sensor.id] // uid/id pour srcIds
      };
      
      console.log('📈 Requête historique:', requestData);
      const result = await _getTagHistories(requestData);
      console.log('📈 Résultat historique:', result);
      
      let rawData: any[] = [];
      
      // Gestion de multiples formats de réponse
      if (result?.success && result.response) {
        console.log("Condition vérifiée");
        rawData = Array.isArray(result.response) ? result.response: [];
      } 
      
      console.log('📈 Données brutes reçues:', rawData.length, 'éléments');
      
      if (rawData.length === 0) {
        console.warn('📈 Aucune donnée d\'historique reçue');
        setSensorHistoriques([]);
        return;
      }
      
      // Traiter le nouveau format avec min/max pré-calculés
      // Regrouper par timestamp et type pour combiner température et humidité
      const groupedByTimestamp: { [key: string]: { 
        timestamp: string, 
        temperature?: number, 
        humidity?: number 
      } } = {};
      
      rawData.forEach((item: any) => {
        // Utiliser datetimezone comme timestamp principal
        const timestamp = item.datetimezone || item.timestamp || new Date().toISOString();
        const timestampKey = timestamp;
        
        if (!groupedByTimestamp[timestampKey]) {
          groupedByTimestamp[timestampKey] = {
            timestamp: timestamp
          };
        }
        
        // Utiliser la moyenne pré-calculée selon le type
        if (item.type === 'temperature') {
          groupedByTimestamp[timestampKey].temperature = parseFloat((item.mean || 0).toFixed(2));
        } else if (item.type === 'humidity' || item.type === 'humidity') {
          groupedByTimestamp[timestampKey].humidity = parseFloat((item.mean || 0).toFixed(2));
        }
      });
      
      // Convertir en tableau de lectures et trier par timestamp
      const readings: SensorReading[] = Object.values(groupedByTimestamp)
        .map(group => ({
          timestamp: group.timestamp,
          temperature: group.temperature || 0,
          humidity: group.humidity || 0
        }))
        .filter(reading => reading.temperature > 0 || reading.humidity > 0) // Filtrer les entrées vides
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      console.log('📈 Données traitées:', readings.length, 'lectures');
      setSensorHistoriques(readings);
      
    } catch (error) {
      console.error('❌ Erreur lors du chargement de l\'historique:', error);
      setSensorHistoriques([]);
    } finally {
      setLoadingHistoriques(false);
    }
  };

  // Charger les capteurs au montage du composant
  useEffect(() => {
    loadCapteurs();
    // Charger les colonnes depuis le localStorage
    const savedColumns = loadColumnsFromStorage();
    setVisibleColumns(savedColumns);
  }, []);

  // Charger l'historique quand le capteur ou les dates changent
  useEffect(() => {
    if (selectedSensor && startDate && endDate) {
      loadSensorHistorique(selectedSensor);
    }
  }, [selectedSensor, startDate, endDate]);

  // Vérifier si on a des données de porte réelles dans l'historique
  const hasDoorData = useMemo(() => {
    return sensorHistoriques.some(entry => 
      (entry as any).porte1Ouvert !== undefined || 
      (entry as any).porte2Ouvert !== undefined
    );
  }, [sensorHistoriques]);

  // Prépare l'historique enrichi avec dépassement de consigne, données de porte et filtrage par période
  const historique: HistoriqueEntry[] = useMemo(() => {
    if (!selectedSensor || sensorHistoriques.length === 0) {
      console.log('🔍 useMemo: Pas de capteur sélectionné ou pas d\'historiques:', {
        selectedSensor: !!selectedSensor,
        sensorHistoriques: sensorHistoriques.length
      });
      return [];
    }
    
    console.log('🔍 useMemo: Traitement des données:', {
      sensorHistoriques: sensorHistoriques.length,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString()
    });
    
    // Utiliser les vraies données chargées depuis l'API
    const filtered = sensorHistoriques
      .filter((entry) => {
        // Gérer les timestamps qui peuvent être en secondes ou millisecondes
        let entryTime: number;
        if (typeof entry.timestamp === 'number') {
          // Si c'est un nombre, vérifier s'il est en secondes (< 1e12) ou millisecondes
          entryTime = entry.timestamp < 1e12 ? entry.timestamp * 1000 : entry.timestamp;
        } else {
          // Si c'est une string, la parser normalement
          entryTime = new Date(entry.timestamp).getTime();
        }
        
        const startOk = !startDate || entryTime >= startDate.getTime();
        const endOk = !endDate || entryTime <= endDate.getTime();
        const isValid = startOk && endOk;
        
        // Log seulement les premières entrées filtrées pour éviter le spam
        if (!isValid && Math.random() < 0.01) { // 1% des entrées filtrées
          console.log('🔍 Entrée filtrée (échantillon):', {
            timestamp: entry.timestamp,
            entryTime,
            startTime: startDate?.getTime(),
            endTime: endDate?.getTime(),
            startOk,
            endOk
          });
        }
        
        return isValid;
      })
      .map((entry) => ({
        timestamp: entry.timestamp,
        temperature: entry.temperature,
        humidity: entry.humidity,
        tempMinExceeded: entry.temperature < (selectedSensor.temperatureMin || 18),
        tempMaxExceeded: entry.temperature > (selectedSensor.temperatureMax || 25),
        humMinExceeded: entry.humidity < (selectedSensor.humidityMin || 40),
        humMaxExceeded: entry.humidity > (selectedSensor.humidityMax || 60),
        // Utiliser les données de porte si elles existent dans l'API
        porte1Ouvert: (entry as any).porte1Ouvert,
        porte2Ouvert: (entry as any).porte2Ouvert,
      }));
    
    console.log('🔍 useMemo: Résultat final:', {
      avant: sensorHistoriques.length,
      après: filtered.length,
      premierTimestamp: sensorHistoriques[0]?.timestamp,
      dernierTimestamp: sensorHistoriques[sensorHistoriques.length - 1]?.timestamp
    });
    
    return filtered;
  }, [selectedSensor, sensorHistoriques, startDate, endDate]);

  console.log('historique:', historique)
  // Formatage des dates
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Badge pour les dépassements de seuils
  const renderThresholdBadge = (value: number, min: number, max: number, unit: string) => {
    if (value < min) {
      return (
        <span className="inline-flex align-items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 border-round text-xs font-medium">
          <i className="pi pi-arrow-down text-xs"></i>
          {value.toFixed(1)}{unit}
        </span>
      );
    } else if (value > max) {
      return (
        <span className="inline-flex align-items-center gap-1 bg-red-100 text-red-800 px-2 py-1 border-round text-xs font-medium">
          <i className="pi pi-arrow-up text-xs"></i>
          {value.toFixed(1)}{unit}
        </span>
      );
    } else {
      return (
        <span className="inline-flex align-items-center gap-1 bg-green-100 text-green-800 px-2 py-1 border-round text-xs font-medium">
          <i className="pi pi-check text-xs"></i>
          {value.toFixed(1)}{unit}
        </span>
      );
    }
  };

  // Templates pour les colonnes de la DataTable
  const timestampTemplate = (rowData: HistoriqueEntry) => {
    return (
      <span className="font-family-monospace white-space-nowrap">
        {new Date(rowData.timestamp).toLocaleString('fr-FR')}
      </span>
    );
  };

  const temperatureTemplate = (rowData: HistoriqueEntry) => {
    const isExceeded = rowData.tempMinExceeded || rowData.tempMaxExceeded;
    return (
      <div className={`flex align-items-center justify-content-center gap-2 ${isExceeded ? 'bg-red-50 text-red-700' : 'text-900'}`}>
        <span className="font-semibold">{rowData.temperature.toFixed(2)}°C</span>
        {rowData.tempMinExceeded && (
          <span className="bg-red-100 text-red-700 px-2 py-1 border-round text-xs font-bold">
            &lt; {selectedSensor?.temperatureMin}°C
          </span>
        )}
        {rowData.tempMaxExceeded && (
          <span className="bg-red-200 text-red-800 px-2 py-1 border-round text-xs font-bold">
            &gt; {selectedSensor?.temperatureMax}°C
          </span>
        )}
      </div>
    );
  };

  const humidityTemplate = (rowData: HistoriqueEntry) => {
    const isExceeded = rowData.humMinExceeded || rowData.humMaxExceeded;
    return (
      <div className={`flex align-items-center justify-content-center gap-2 ${isExceeded ? 'bg-blue-50 text-blue-700' : 'text-900'}`}>
        <span className="font-semibold">{rowData.humidity.toFixed(2)}%</span>
        {rowData.humMinExceeded && (
          <span className="bg-blue-100 text-blue-700 px-2 py-1 border-round text-xs font-bold">
            &lt; {selectedSensor?.humidityMin}%
          </span>
        )}
        {rowData.humMaxExceeded && (
          <span className="bg-blue-200 text-blue-800 px-2 py-1 border-round text-xs font-bold">
            &gt; {selectedSensor?.humidityMax}%
          </span>
        )}
      </div>
    );
  };

  const depassementTemplate = (rowData: HistoriqueEntry) => {
    return (
      <div className="flex flex-wrap gap-1 justify-content-center">
        {rowData.tempMinExceeded && (
          <span className="bg-red-100 text-red-700 px-2 py-1 border-round text-xs font-semibold">
            <i className="pi pi-thermometer mr-1"></i>Temp &lt; {selectedSensor?.temperatureMin}°C
          </span>
        )}
        {rowData.tempMaxExceeded && (
          <span className="bg-red-200 text-red-800 px-2 py-1 border-round text-xs font-semibold">
            <i className="pi pi-thermometer mr-1"></i>Temp &gt; {selectedSensor?.temperatureMax}°C
          </span>
        )}
        {rowData.humMinExceeded && (
          <span className="bg-blue-100 text-blue-700 px-2 py-1 border-round text-xs font-semibold">
            <i className="fa-duotone fa-regular fa-droplet-degree mr-1"></i>Hum &lt; {selectedSensor?.humidityMin}%
          </span>
        )}
        {rowData.humMaxExceeded && (
          <span className="bg-blue-200 text-blue-800 px-2 py-1 border-round text-xs font-semibold">
            <i className="fa-duotone fa-regular fa-droplet-degree mr-1"></i>Hum &gt; {selectedSensor?.humidityMax}%
          </span>
        )}
        {!(rowData.tempMinExceeded || rowData.tempMaxExceeded || rowData.humMinExceeded || rowData.humMaxExceeded) && (
          <span className="bg-green-100 text-green-700 px-2 py-1 border-round text-xs font-semibold">
            <i className="pi pi-check mr-1"></i>OK
          </span>
        )}
      </div>
    );
  };

  let columns = useMemo(()=>[
      {
          "field": "timestamp",
          "header": "Horodatage",
          body: timestampTemplate,
          "sortable": true,
          "style": {
              "minWidth": "200px"
          },
          "headerStyle": {
              "textAlign": "left"
          }
      },
      {
          "field": "humidity",
          "header": "Humidité (%)",
          body: temperatureTemplate,
          "sortable": true,
          "style": {
              "minWidth": "150px"
          },
          "headerStyle": {
              "textAlign": "center"
          }
      },
      {
          "field": "temperature",
          "header": "Température (°C)",
          body: humidityTemplate,
          "sortable": true,
          "style": {
              "minWidth": "150px"
          },
          "headerStyle": {
              "textAlign": "center"
          }
      },
      {
          "field": "depassements",
          "header": "Dépassements",
          body: depassementTemplate,
          "sortable": false,
          "style": {
              "minWidth": "200px"
          },
          "headerStyle": {
              "textAlign": "center"
          }
      }
  ], [selectedSensor])

  // Barre d'outils avec filtrage et export
  const renderHeader = () => {
    return (
      <div className="flex flex-wrap align-items-center justify-content-between gap-3 p-3 bg-white border-round-top">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-database text-primary"></i>
          <h3 className="text-900 font-semibold m-0">
            Historique - {selectedSensor?.label} ({historique.length} enregistrement(s))
          </h3>
        </div>
        
        <div className="flex flex-wrap align-items-center gap-2">
          {/* Champ de recherche globale */}
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Rechercher..."
              className="w-full sm:w-auto"
            />
          </span>
          
          {/* Boutons d'export et configuration */}
          <div className="flex gap-1">
            {/* Bouton de configuration des colonnes */}
            <Button
              type="button"
              icon="pi pi-table"
              rounded
              onClick={() => setShowColumnSelector(true)}
              tooltip="Configurer les colonnes"
              className="p-button-help p-button-outlined"
              size="small"
            />
            
            {/* Boutons d'export */}
            <Button
              type="button"
              icon="pi pi-file"
              rounded
              onClick={exportCSV}
              data-pr-tooltip="CSV"
              tooltip="Exporter en CSV"
              className="p-button-success p-button-outlined"
              size="small"
            />
            <Button
              type="button"
              icon="pi pi-file-excel"
              rounded
              onClick={exportExcel}
              data-pr-tooltip="XLS"
              tooltip="Exporter en Excel"
              className="p-button-success p-button-outlined"
              size="small"
            />
            <Button
              type="button"
              icon="pi pi-file-pdf"
              rounded
              onClick={exportPdf}
              data-pr-tooltip="PDF"
              tooltip="Exporter en PDF"
              className="p-button-danger p-button-outlined"
              size="small"
            />
          </div>
        </div>
      </div>
    );
  };

  // Affichage du loader pour les capteurs
  if (loadingCapteurs) {
    return (
      <div className="p-4">
        <div className="surface-card border-round-lg shadow-3 p-6">
          <div className="flex flex-column align-items-center justify-content-center" style={{ minHeight: '200px' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
            <p className="mt-4 text-600">Chargement des capteurs...</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="p-4">
      {/* Page Header */}
      {/* @ts-ignore */}
      <PageHeader
        titleOlang="sensor.history"
        backPath="/Capteurs/index"
        info={
          <div className="flex align-items-center gap-3">
            {selectedSensor && (
              <div className="flex align-items-center gap-2">
                <i className="pi pi-microchip text-xl text-primary"></i>
                <span className="text-lg font-semibold">{selectedSensor.label}</span>
              </div>
            )}
            {sensorHistoriques.length > 0 && (
              <>
                <div className="border-left-2 border-300" style={{ height: '30px' }}></div>
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-database text-xl text-primary"></i>
                  <span className="text-lg font-semibold">
                    {sensorHistoriques.length} enregistrement{sensorHistoriques.length > 1 ? 's' : ''}
                  </span>
                </div>
              </>
            )}
          </div>
        }
        className="mb-3"
      />
      
      <div className="surface-card border-round-lg shadow-3 p-6">

        {/* Sélection du capteur - Mise en avant */}
        <div className="surface-card border-round-lg shadow-4 p-4 mb-4 border-2 border-primary-200">
          <div className="flex align-items-center gap-3 mb-3">
            <div className="bg-primary-50 border-circle p-3">
              <i className="pi pi-microchip text-primary text-2xl"></i>
            </div>
            <div>
              <h3 className="text-900 font-bold text-xl m-0">Choisir un capteur</h3>
              <p className="text-600 m-0 text-sm">Sélectionnez le capteur dont vous voulez consulter l'historique</p>
            </div>
          </div>

          <Dropdown
            value={selectedSensor}
            options={capteurs}
            onChange={(e) => {
              setSelectedSensor(e.value);
            }}
            optionLabel="label"
            placeholder="🔍 Sélectionner un capteur..."
            className="w-full"
            showClear
            filter
            filterPlaceholder="Rechercher un capteur..."
            emptyMessage="Aucun capteur disponible"
            style={{ fontSize: '1.1rem', minHeight: '3.5rem' }}
          />
          {!selectedSensor && (
            <div className="mt-3 p-3 bg-orange-50 border-round border-1 border-orange-200">
              <div className="flex align-items-center">
                <i className="pi pi-info-circle text-orange-500 mr-2"></i>
                <span className="text-orange-700 text-sm font-medium">
                  Veuillez d'abord sélectionner un capteur pour afficher son historique
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* Filtres de période - Affichés seulement si un capteur est sélectionné */}
        {selectedSensor && (
          <div className="surface-100 border-round p-4 mb-4">
            <div className="flex align-items-center gap-2 mb-3">
              <i className="pi pi-calendar text-600"></i>
              <h4 className="text-900 font-semibold m-0">Période d'analyse</h4>
            </div>

            {/* Navigation par jour (affichée seulement si pas de période personnalisée) */}
            {!isCustomPeriod && (
              <div className="flex align-items-center justify-content-between mb-4 p-3 bg-blue-50 border-round border-1 border-blue-200">
                <Button
                  icon="pi pi-chevron-left"
                  label="Jour précédent"
                  onClick={goToPreviousDay}
                  className="p-button-outlined p-button-sm"
                  size="small"
                />
                
                <div className="text-center">
                  <div className="text-900 font-semibold text-lg">
                    {selectedDate.toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    {isToday(selectedDate) && (
                      <span className="ml-2 text-sm text-green-600 font-normal">
                        (Aujourd'hui)
                      </span>
                    )}
                  </div>
                </div>
                
                <Button
                  icon="pi pi-chevron-right"
                  label="Jour suivant"
                  iconPos="right"
                  onClick={goToNextDay}
                  disabled={isFutureDate(selectedDate)}
                  className="p-button-outlined p-button-sm"
                  size="small"
                />
              </div>
            )}

            {/* Sélection de période personnalisée */}
            <div className="grid">
              <div className="col-12 md:col-4">
                <label className="block text-900 font-medium mb-2">
                  <i className="pi pi-calendar mr-2"></i>
                  Date de début
                </label>
                <Calendar
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.value as Date)}
                  showTime
                  className="w-full"
                  placeholder="Sélectionner la date de début"
                  dateFormat="dd/mm/yy"
                  hourFormat="24"
                  stepMinute={15}
                  showIcon
                />
              </div>
              <div className="col-12 md:col-4">
                <label className="block text-900 font-medium mb-2">
                  <i className="pi pi-calendar mr-2"></i>
                  Date de fin
                </label>
                <Calendar
                  value={endDate}
                  onChange={(e) => handleEndDateChange(e.value as Date)}
                  showTime
                  className="w-full"
                  placeholder="Sélectionner la date de fin"
                  dateFormat="dd/mm/yy"
                  hourFormat="24"
                  stepMinute={15}
                  showIcon
                />
              </div>
              <div className="col-12 md:col-4 flex align-items-end">
                {/* Bouton pour revenir au mode 24h (affiché seulement en mode période personnalisée) */}
                {isCustomPeriod && (
                  <Button
                    icon="pi pi-refresh"
                    label="Mode 24h"
                    onClick={resetTo24HourMode}
                    className="p-button-success p-button-outlined w-full"
                    size="small"
                    tooltip="Revenir au mode 24h avec navigation par jour"
                  />
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Données du capteur sélectionné */}
        {selectedSensor && (
          <div className="surface-0 border-round shadow-2">
            {/* En-tête de la section */}
            <div className="surface-100 border-round-top p-3 border-bottom-1 surface-border">
              <div className="flex align-items-center justify-content-between">
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-database text-primary"></i>
                  <h3 className="text-900 font-semibold m-0">Historique - {selectedSensor.label}</h3>
                </div>
                <div className="flex align-items-center gap-2">
                  <span className="text-500 text-sm">{historique.length} enregistrement(s)</span>
                </div>
              </div>
            </div>
            {/* Contenu */}
            {loadingHistoriques ? (
              <div className="flex flex-column align-items-center justify-content-center p-6">
                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                <p className="mt-3 text-600">Chargement de l'historique...</p>
              </div>
            ) : historique.length === 0 ? (
              <div className="flex flex-column align-items-center justify-content-center p-6">
                <i className="pi pi-info-circle text-400 text-4xl mb-3"></i>
                <p className="text-600 text-center">
                  Aucune donnée disponible pour la période sélectionnée.
                  <br />
                  Essayez de modifier les dates ou sélectionner un autre capteur.
                </p>
              </div>
            ) : (
              <div className="p-0">
                {/* DataTable avec filtrage et export */}
                <DataTableComponent 
                  tableId={'histories-graph-list'}
                  data={historique}
                  columns={columns}
                />
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Toast pour les notifications d'export */}
      <Toast ref={toast} />
      
      {/* Sidebar pour la configuration des colonnes */}
      <Sidebar
        visible={showColumnSelector}
        position="right"
        onHide={() => setShowColumnSelector(false)}
        className="w-full md:w-20rem lg:w-25rem"
        modal
      >
        <HistoriqueColumnSelector
          columns={ALL_COLUMNS}
          visibleColumns={visibleColumns}
          onColumnChange={handleColumnChange}
          hasDoorData={hasDoorData}
        />
      </Sidebar>
    </div>
  );
};

export default Historique;
